/**
 * The one AudioContext of the app, shared by the game audio and the voice chat (one output
 * chain for everything, and iOS echo cancellation hears the game through the same session).
 *
 * Browsers start audio only after a user gesture, and iOS also stops it on interruptions
 * (a call, Siri, the app going to the background). `installUnlock()` therefore listens to
 * every tap and key press in the capture phase, for the whole life of the app: while the
 * context is not running, a gesture creates or resumes it and plays one silent sample (the
 * old iOS unlock trick); while it runs, the listener returns at once. Until then
 * `runningContext()` is null, and the game audio treats every call as a cheap no-op.
 *
 * While the page is hidden and nobody holds the context (the voice chat does while it is
 * on), the context is suspended to save the battery; it resumes when the page is visible.
 */
import { type ReadonlySignal, type Signal, signal } from '@preact/signals';
import { createOutputChain, type OutputChain } from './output';
import { createNoiseBuffer } from './synth';

/** Events that carry user activation in every browser we target, iOS Safari included. */
export const GESTURE_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'] as const;

export interface AudioHostEnvironment {
  /** Creates the AudioContext, or returns null where Web Audio is missing. */
  createContext(): AudioContext | null;
  /** Gesture and visibility listeners go here; null outside a browser (tests, SSR). */
  document: Document | null;
  /** For `pageshow` (back/forward cache); null outside a browser. */
  window: Window | null;
  /**
   * The page is driven by automation (navigator.webdriver: the e2e, the QA tour, screenshot
   * scripts). Everything runs as usual, but nothing reaches the speakers: those runs share
   * the computer with the person working at it.
   */
  automated?: boolean;
}

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

/** The real browser environment (Web Audio may still be missing: then nothing plays). */
export function browserAudioEnvironment(): AudioHostEnvironment {
  const win = typeof window === 'undefined' ? null : window;
  return {
    createContext() {
      const Ctor = win ? (win.AudioContext ?? (win as WebkitWindow).webkitAudioContext) : undefined;
      if (!Ctor) return null;
      try {
        return new Ctor({ latencyHint: 'interactive' });
      } catch {
        return null;
      }
    },
    document: typeof document === 'undefined' ? null : document,
    window: win,
    automated: win?.navigator.webdriver === true,
  };
}

export class AudioHost {
  private ctx: AudioContext | null = null;
  private chain: OutputChain | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private readonly runningSignal: Signal<boolean> = signal(false);
  private readonly remoteVoiceSignal: Signal<boolean> = signal(false);
  private holds = 0;
  private uninstall: (() => void) | null = null;

  /** True while the context runs (unlocked and not interrupted). */
  readonly running: ReadonlySignal<boolean> = this.runningSignal;
  /** Other players are talking in the voice chat: the game music ducks under their voices. */
  readonly remoteVoiceActive: ReadonlySignal<boolean> = this.remoteVoiceSignal;

  constructor(private readonly env: AudioHostEnvironment) {}

  /** The context if it was created (in any state). */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Nothing reaches the speakers (see AudioHostEnvironment.automated). */
  get silent(): boolean {
    return this.env.automated === true;
  }

  /** The context only while it runs; null means "play nothing now". */
  runningContext(): AudioContext | null {
    return this.ctx !== null && this.ctx.state === 'running' ? this.ctx : null;
  }

  /** Creates the context on first use (or after it was closed). */
  ensureContext(): AudioContext | null {
    if (this.ctx && this.ctx.state !== 'closed') return this.ctx;
    const ctx = this.env.createContext();
    if (!ctx) return null;
    this.ctx = ctx;
    this.chain = null;
    this.noiseBuffer = null;
    ctx.onstatechange = () => this.syncState();
    this.syncState();
    return ctx;
  }

  /** Input of the master output chain on the current context (built on first use). */
  output(): AudioNode | null {
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'closed') return null;
    this.chain ??= createOutputChain(ctx, this.silent ? mutedSink(ctx) : ctx.destination);
    return this.chain.input;
  }

  /** A second of white noise for drums and effects, shared by everybody. */
  noise(): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    this.noiseBuffer ??= createNoiseBuffer(ctx);
    return this.noiseBuffer;
  }

  /**
   * Starts audio output. Call it from inside a user gesture (the installed listeners do,
   * and the voice chat's enable() does too): creates or resumes the context and plays a
   * silent sample, which is what unlocks output on older iOS versions.
   */
  unlock(): void {
    const ctx = this.ensureContext();
    if (!ctx || ctx.state === 'running') return;
    ctx.resume().then(
      () => this.syncState(),
      () => {
        // Not allowed yet (no gesture): the next tap tries again.
      },
    );
    playSilence(ctx);
  }

  /** Voice chat: keeps the context running while the page is hidden. Returns the release. */
  hold(): () => void {
    this.holds += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds -= 1;
    };
  }

  /** Set by the voice chat: somebody else is talking right now. */
  setRemoteVoiceActive(active: boolean): void {
    this.remoteVoiceSignal.value = active;
  }

  /** Registers the gesture, visibility and back/forward-cache listeners (once). */
  installUnlock(): () => void {
    if (this.uninstall) return this.uninstall;
    const { document: doc, window: win } = this.env;
    if (!doc) return () => {};
    const onGesture = () => {
      if (this.ctx?.state !== 'running') this.unlock();
    };
    const onVisibility = () => this.onVisibility(doc.visibilityState === 'visible');
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) this.onVisibility(true);
    };
    const options: AddEventListenerOptions = { capture: true, passive: true };
    for (const type of GESTURE_EVENTS) doc.addEventListener(type, onGesture, options);
    doc.addEventListener('visibilitychange', onVisibility);
    win?.addEventListener('pageshow', onPageShow);
    this.uninstall = () => {
      for (const type of GESTURE_EVENTS) doc.removeEventListener(type, onGesture, options);
      doc.removeEventListener('visibilitychange', onVisibility);
      win?.removeEventListener('pageshow', onPageShow);
      this.uninstall = null;
    };
    return this.uninstall;
  }

  private onVisibility(visible: boolean): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'closed') return;
    if (!visible) {
      if (this.holds === 0 && ctx.state === 'running') ctx.suspend().catch(() => {});
      return;
    }
    // Back on screen (also after an iOS interruption): resuming may need a new gesture,
    // in which case the gesture listeners do it.
    if (ctx.state !== 'running') {
      ctx.resume().then(
        () => this.syncState(),
        () => {},
      );
    }
  }

  private syncState(): void {
    this.runningSignal.value = this.ctx?.state === 'running';
  }
}

/** A gain of 0 in front of the speakers: the whole graph runs, nothing is heard. */
function mutedSink(ctx: AudioContext): GainNode {
  const sink = ctx.createGain();
  sink.gain.value = 0;
  sink.connect(ctx.destination);
  return sink;
}

/** A one-sample silent buffer started inside the gesture. */
function playSilence(ctx: AudioContext): void {
  try {
    const source = ctx.createBufferSource();
    source.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    source.connect(ctx.destination);
    source.onended = () => source.disconnect();
    source.start(0);
  } catch {
    // A closed or broken context: nothing to unlock.
  }
}

/** The app-wide host. */
export const audioHost = new AudioHost(browserAudioEnvironment());

/** Installed by the shell at startup; see AudioHost.installUnlock. */
export function installAudioUnlock(): () => void {
  return audioHost.installUnlock();
}
